import { gsap } from "gsap"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import SplitType from 'split-type'

/**
 * Клас для ініціалізації GSAP
 * 
 * @package AGDS
 * @category Modules
 * @module GSAP
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class GSAP {

	/**
	 * Конструктор
	 *
	 * @param {Object} options
	 */
	constructor(options = {}) { }

	/**
	 * Ініціалізація
	 */
	init() {
		if (typeof gsap === 'undefined') return

		// Реєстрація плагінів
		gsap.registerPlugin(MotionPathPlugin, ScrollTrigger)

		// Анімація Front Hero
		this.frontHeroInit()

		// Анімація зображень
		this.imageRevealAndParallaxInit()

		// Анімація заголовків
		this.titleLinesAnimationInit()

		// Анімація розширення центрального фото
		this.experienceExpandInit()

		// Анімації Products
		this.productsScrollXInit()

		// Icons Environment
		this.iconsEnvironmentInit()

		// Cлот-машина
		this.slotMachineInit()

		// Parallax
		this.parallaxInit()

		// Анімації лічильників
		this.numberCounterInit()
	}

	/**
	 * Анімація Front Hero
	 */
	frontHeroInit() {
		const section = document.querySelector('.front-hero-section')
		if (!section) return

		const titleChars = section.querySelectorAll('.title .char')
		const descWrap = section.querySelector('.desc-wrap')
		const descChars = section.querySelectorAll('.desc .char')
		const scrollDown = section.querySelectorAll('.scroll-down-link')
		const rbThreadAnimation = section.querySelector('.rb-thread-animation')

		// 2. Створюємо GSAP Таймлайн
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: section,
				start: "top top", // Старт, коли секція доходить до верху екрана
				end: "+=250%",    // Довжина скролу (200% від висоти екрана). Можна збільшити, якщо хочеться повільніше
				pin: true,        // Закріплюємо секцію
				scrub: 1,         // Плавний скрол-зв'язок (smooth scrubbing)
				invalidateOnRefresh: true
			}
		})

		// Етап 1: Заголовок "ховається" вгору, починаючи з країв до центру
		if (titleChars.length > 0) {
			tl.to(titleChars, {
				yPercent: -100, // Піднімаємо вгору (маска clip-path його сховає)
				opacity: 0,     // Додатково зменшуємо прозорість для плавності
				stagger: {
					each: 0.05,
					from: "edges", // КЛЮЧОВИЙ МОМЕНТ: Анімація від країв (0 та довжина) до центру
				},
				ease: "power2.inOut"
			})
		}

		// 2. Scroll down зникає трохи швидше
		if (scrollDown) {
			tl.to(scrollDown, {
				yPercent: -100,
				opacity: 0,
				duration: 0.1,
				ease: "power2.inOut"
			}, 0.3)
		}

		// Поява обгортки опису
		if (descWrap) {
			tl.set(descWrap, { autoAlpha: 1 }, "-=0.2")
		}

		// Заповнення літер опису (від 0.5 до 1)
		if (descChars.length > 0) {
			tl.to(descChars, {
				opacity: 1,
				stagger: 0.05, // Кожна літера загоряється по черзі
				ease: "none"
			})
		}

		// Виконується відразу після завершення попереднього кроку
		if (descWrap) {
			tl.to(descWrap, {
				y: -40,          // Трохи піднімаємо вверх
				opacity: 0,      // Зникає
				duration: 0.5,   // Робимо фазу зникнення відчутною
				ease: "power2.inOut"
			})
		}

		// Виконується відразу після завершення попереднього кроку
		if (rbThreadAnimation) {
			tl.to(rbThreadAnimation, {
				opacity: 0,
				duration: 0.5,
				ease: "power2.inOut"
			}, "<")
		}

	}

	/**
	 * Анімація зображень та Parallax
	 */
	imageRevealAndParallaxInit() {
		const imageWraps = document.querySelectorAll('.image-reveal-parallax')
		if (!imageWraps.length) return

		imageWraps.forEach((wrap) => {
			const img = wrap.querySelector('.image')
			if (!img) return

			// Анімація появи (Reveal)
			gsap.to(wrap, {
				clipPath: "inset(0%)", // Розкриваємо маску повністю
				opacity: 1,
				duration: 2,
				ease: "power3.out",
				scrollTrigger: {
					trigger: wrap,
					start: "top 85%", // Починається, коли верх картинки на 85% екрана
					toggleActions: "play none none none" // Програється лише один раз
				}
			})

			// Вертикальний паралакс (Parallax)
			gsap.set(img, { yPercent: -7 })

			gsap.to(img, {
				yPercent: 7, // Рухаємо вниз відносно свого розміру (динамічно для будь-яких розмірів!)
				ease: "none", // Важливо: для скрол-паралаксу ease завжди "none", щоб рух був лінійним
				scrollTrigger: {
					trigger: wrap,
					start: "top bottom", // Починаємо скрол-зв'язок, як тільки блок з'явився знизу екрана
					end: "bottom top",   // Закінчуємо, коли блок сховався зверху
					scrub: 1             // Плавна прив'язка до скролу (1 секунда затримки для плавності)
				}
			})
		})
	}

	/**
	 * Анімація заголовків
	 */
	titleLinesAnimationInit() {
		const textElements = document.querySelectorAll('.title-line-animate')
		if (typeof SplitType === 'undefined' || !textElements.length) return

		textElements.forEach((el) => {
			// Розбиваємо текст на рядки
			const split = new SplitType(el, {
				types: "lines, words",
			})

			// Обгортаємо кожен рядок у маску (line-wrapper)
			split.lines.forEach((line) => {
				const wrapper = document.createElement('div')
				wrapper.className = 'line-wrapper'
				line.parentNode.insertBefore(wrapper, line)
				wrapper.appendChild(line)
			})

			el.classList.add('is-initialized')

			// Створюємо анімацію появи
			const lines = el.querySelectorAll('.line')
			gsap.to(lines, {
				y: 0,
				duration: 2,
				ease: "power3.out",
				stagger: 0.15, // Затримка між рядками
				scrollTrigger: {
					trigger: el,
					start: "top 85%", // Починати, коли заголовок на 85% висоти екрана
					toggleActions: "play none none none" // Анімувати лише один раз
				}
			})
		})
	}

	/**
	 * Анімація розширення центрального фото на весь екран
	 */
	experienceExpandInit() {
		const section = document.querySelector('.about-experience-section')
		if (!section) return

		const pinWrapper = section?.querySelector('.wrapper')
		const bgImageWrap = section?.querySelector('.bg-image-wrap')
		const bgImage = bgImageWrap?.querySelector('img')

		const placeholder = section?.querySelector('.image-placeholder')
		const leftCol = section?.querySelector('.col-left')
		const rightCol = section?.querySelector('.col-right')
		const title = section?.querySelector('.title')

		if (!pinWrapper || !bgImageWrap || !placeholder) return

		const isDesktop = window.matchMedia("(min-width: 1024px)").matches

		// 1. Функція для розрахунку координат clip-path
		const getInitialClipPath = () => {
			const rect = placeholder.getBoundingClientRect()
			const container = pinWrapper.getBoundingClientRect()

			const top = rect.top - container.top
			const left = rect.left - container.left
			const x = container.width - (left + rect.width)
			const bottom = container.height - (top + rect.height)

			return `inset(${top}px ${x}px ${bottom}px)`
		};

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: pinWrapper,
				start: isDesktop ? "top top" : "center center",
				end: "+=150%",
				pin: true,
				scrub: 1,
				invalidateOnRefresh: true,
				// markers: true
			}
		})

		// Використовуємо функціональне значення, щоб при оновленні (refresh) 
		tl.fromTo(bgImageWrap,
			{
				clipPath: () => getInitialClipPath()
			},
			{
				clipPath: "inset(0px 0px 0px)",
				ease: "none",
				immediateRender: false
			},
			0
		)

		tl.to(bgImage, {
			scale: 1,
			ease: "none"
		}, 0);

		tl.to(leftCol, { xPercent: -150, opacity: 0.3, ease: "none" }, 0)
		tl.to(rightCol, { xPercent: 150, opacity: 0.3, ease: "none" }, 0)
		tl.to(title, { y: -100, opacity: 0, ease: "power2.inOut" }, 0)

		return () => {
			gsap.set([bgImageWrap, leftCol, rightCol, title, bgImage], { clearProps: "all" })
		}
	}

	/**
	 * Горизонтальний скрол продуктів з динамічним gap
	 */
	productsScrollXInit() {
		const section = document.querySelector('.front-products-section');
		if (!section) return;

		const wrapper = section.querySelector('.products-slider-wrapper');
		const items = section.querySelectorAll('.products-slider-item');
		const title = section.querySelector('.title');
		if (!wrapper || !items.length) return;

		let mm = gsap.matchMedia();

		mm.add("(min-width: 768px)", () => {
			// 1. Створюємо високошвидкісний сетер для scale
			const scaleSetter = gsap.quickTo(items, "scale", { duration: 0.3, ease: "power2.out" });

			// Початкові стани
			gsap.set(items, { opacity: 0, x: 100, transformOrigin: "center center" });
			gsap.set(wrapper, { x: () => window.innerWidth * 0.9 });

			const getScrollAmount = () => {
				let totalWidth = wrapper.scrollWidth;
				return -(totalWidth - window.innerWidth + 50);
			};

			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: section,
					start: "top 15%",
					end: () => `+=${wrapper.scrollWidth}`,
					pin: true,
					scrub: 1,
					invalidateOnRefresh: true,
					// markers: true,
					onUpdate: (self) => {
						// Отримуємо швидкість скролу (зазвичай від 0 до ~2000)
						const velocity = Math.abs(self.getVelocity());

						// Мапимо швидкість у scale:
						// 0 швидкості = 1 scale
						// 1500 швидкості = 0.9 scale
						let mappedScale = gsap.utils.mapRange(0, 1500, 1, 0.9, velocity);

						// Обмежуємо значення, щоб при дуже різкому скролі не стало менше 0.9
						const clampedScale = gsap.utils.clamp(0.9, 1, mappedScale);

						// scaleSetter(clampedScale);
					},
					onToggle: (self) => {
						// Якщо скрол зупинився або секція закінчилася, плавно повертаємо до 1
						// if (!self.isActive) scaleSetter(1);
					}
				}
			});

			// 1. ГОЛОВНИЙ РУХ (займає 75% таймлайну)
			tl.to(wrapper, {
				x: getScrollAmount,
				duration: 0.75, // Синхронізуємо рух з вильотом
				ease: "none"
			}, 0);

			// 2. ПОЯВА АЙТЕМІВ
			tl.to(items, {
				opacity: 1,
				x: 0,
				duration: 0.2,
				stagger: 0.02,
				ease: "power1.out"
			}, 0);

			// 3. ЗНИКНЕННЯ ЗАГОЛОВКА
			tl.to(title, {
				y: -80,
				opacity: 0,
				duration: 0.2,
				ease: "power1.in"
			}, 0.6); // Починається на 50% скролу

			// 4. ВЕРТИКАЛЬНИЙ ВИЛІТ ВГОРУ
			tl.to(items, {
				y: -window.innerHeight - 50,
				opacity: 0,
				duration: 0.3,
				stagger: {
					amount: 0.15, // ВАЖЛИВО: amount замість each. Весь стагер займе 0.15с
					from: "end"
				},
				ease: "power2.in"
			}, 0.65); // Починається трохи раніше завершення горизонтального руху

			return () => {
				gsap.set([wrapper, items, title], { clearProps: "all" });
			};
		});
	}

	/**
	 * Анімація появи, левітації та паралаксу іконок
	 */
	iconsEnvironmentInit() {
		const section = document.querySelector('.front-icons-section');
		if (!section) return;

		const title = section.querySelector('.title');
		const largeIcons = section.querySelectorAll('.icon--large');
		const mediumIcons = section.querySelectorAll('.icon--medium');
		const smallIcons = section.querySelectorAll('.icon--small');
		const decorEls = section.querySelectorAll('.icon--decor');

		const allParallaxWraps = section.querySelectorAll('.icon-parallax-wrap');
		const allLevitateWraps = section.querySelectorAll('.icon-levitate');

		// Фікс для GSAP: щоб він не конфліктував з CSS `transform: translate(-50%, -50%)`,
		// ми кажемо йому зафіксувати це зміщення на старті
		gsap.set(allParallaxWraps, { xPercent: -50, yPercent: -50 });

		// 1. ТАЙМЛАЙН ПОЯВИ
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: section,
				start: "top 60%", // Запуск, коли секція займає 60% екрана
			}
		});

		// Крок 1: Поява тексту (повільніше)
		tl.fromTo(title,
			{ opacity: 0, y: 30 },
			{ opacity: 1, y: 0, duration: 1.2, ease: "power2.out" }
		);

		tl.fromTo(decorEls,
			{ opacity: 0, scale: 0.5 },
			{ opacity: 0.3, scale: 1, duration: 1, stagger: 0.2 },
			"-=2"
		);

		// Крок 2: З'являються ВЕЛИКІ іконки (stagger: 0.15 робить паузу між кожною)
		tl.fromTo(largeIcons,
			{ opacity: 0, scale: 0 },
			{ opacity: 1, scale: 1, duration: 0.8, stagger: 0.15, ease: "back.out(1.5)" },
			"-=0.1"
		);

		// Крок 3: З'являються СЕРЕДНІ іконки
		tl.fromTo(mediumIcons,
			{ opacity: 0, scale: 0 },
			{ opacity: 1, scale: 1, duration: 0.8, stagger: 0.15, ease: "back.out(1.3)" },
			"-=0.1" // Запуск трохи до закінчення великих
		);

		// Крок 4: З'являються МАЛЕНЬКІ іконки
		tl.fromTo(smallIcons,
			{ opacity: 0, scale: 0 },
			{ opacity: 1, scale: 1, duration: 0.8, stagger: 0.1, ease: "back.out(1.2)" },
			"-=0.1"
		);

		// 2. ЗАПУСК ЛЕВІТАЦІЇ (після повної появи всіх елементів)
		tl.add(() => {
			allLevitateWraps.forEach((el) => {
				gsap.to(el, {
					y: gsap.utils.random(-15, 15),
					x: gsap.utils.random(-10, 10),
					rotation: gsap.utils.random(-4, 4),
					duration: gsap.utils.random(3, 5), // Спокійніший рух
					delay: gsap.utils.random(0, 2),
					ease: "sine.inOut",
					yoyo: true,
					repeat: -1
				});
			});
		});

		// 3. ПАРАЛАКС ВІД КУРСОРА
		const onMouseMove = (e) => {
			const { innerWidth, innerHeight } = window;
			const xPos = (e.clientX / innerWidth - 0.5) * 2;
			const yPos = (e.clientY / innerHeight - 0.5) * 2;

			allParallaxWraps.forEach((wrap) => {
				const speed = parseFloat(wrap.dataset.speed) || 1;

				gsap.to(wrap, {
					x: xPos * -35 * speed,
					y: yPos * -35 * speed,
					duration: 0.8, // Плавніше слідування за мишкою
					ease: "power2.out",
					overwrite: "auto"
				});
			});
		};

		section.addEventListener("mousemove", onMouseMove);

		return () => {
			section.removeEventListener("mousemove", onMouseMove);
			gsap.killTweensOf(allLevitateWraps);
			gsap.killTweensOf(allParallaxWraps);
		};
	}

	/**
	 * Cлот-машина
	 */
	slotMachineInit() {
		const slots = document.querySelectorAll('[data-slot-machine] .slot')
		if (slots.length === 0) return

		const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
		const loops = 1; // кількість повних прокруток барабана

		slots.forEach((slot, index) => {
			const target = slot.getAttribute('data-target')
			const targetIndex = digits.indexOf(String(target))

			// Будуємо барабан з повторюваних цифр
			const reel = document.createElement('div')
			reel.classList.add('slot--reel')

			const totalSymbols = loops * digits.length + digits.length
			let html = ''
			for (let i = 0; i < totalSymbols; i++) {
				html += `<div class="slot--number">${digits[i % digits.length]}</div>`
			}
			reel.innerHTML = html

			slot.innerHTML = ''
			slot.appendChild(reel)

			// Скидаємо позицію перед кожним запуском
			gsap.set(reel, { y: 0 })

			const symbolHeight = slot.offsetHeight

			// Зупиняємось точно на потрібній цифрі
			const stopIndex = loops * digits.length + targetIndex
			const y = -(symbolHeight * stopIndex)

			gsap.to(reel, {
				y,
				duration: 2.5 + index * 0.8,  // кожен барабан зупиняється пізніше
				ease: "power3.out",            // плавне уповільнення без відскоку
				delay: 0.1 * index,
			});
		});
	}

	/**
	 * Універсальний паралакс на базі ScrollTrigger
	 */
	parallaxInit() {
		const elements = document.querySelectorAll('[data-parallax], [data-parallax-rate]')
		if (!elements.length) return

		elements.forEach(el => {
			const rawRate = el.dataset.parallaxRate || el.dataset.parallax || "15%"
			const rate = isNaN(rawRate) ? rawRate : `${rawRate}px`
			const direction = el.dataset.parallaxDirection || "y"

			gsap.fromTo(el,
				{ [direction]: 0 },
				{
					[direction]: rate,
					ease: "none",
					scrollTrigger: {
						trigger: el,
						start: "top bottom",
						end: "bottom top",
						scrub: 1.2,
						invalidateOnRefresh: true,
						invalidateOnRefresh: true
					}
				}
			)
		})
	}

	/**
	 * Анімації лічильників
	 */
	numberCounterInit() {
		const counters = document.querySelectorAll('[data-number-counter] .number')
		if (!counters.length) return

		counters.forEach(counter => {
			// Отримуємо текстове значення з HTML і очищаємо від пробілів
			const textValue = counter.innerHTML.trim()

			// Перетворюємо рядок на число (допускаємо, що розділювач дробів - крапка)
			const targetValue = parseFloat(textValue)

			// Якщо всередині не число (наприклад, пустий рядок), пропускаємо
			if (isNaN(targetValue)) return

			// Визначаємо кількість знаків після коми (якщо вони є)
			// Це потрібно, щоб 3.44 не перетворилось на 3 або 3.40000001
			let decimals = 0
			if (textValue.includes('.')) {
				decimals = textValue.split('.')[1].length
			}

			// Створюємо стартовий об'єкт для GSAP
			const animObj = { val: 0 }

			// Одразу скидаємо значення в DOM на нуль у правильному форматі
			counter.innerText = (0).toFixed(decimals)

			// Запускаємо анімацію з прив'язкою до скролу
			gsap.to(animObj, {
				val: targetValue,
				duration: 4,           // Тривалість анімації (2 секунди)
				ease: "power2.out",    // Плавне сповільнення в кінці
				scrollTrigger: {
					trigger: counter,
					start: "top 85%",  // Анімація почнеться, коли елемент з'явиться на 15% знизу екрана
				},
				onUpdate: () => {
					// На кожному кадрі анімації оновлюємо текст з потрібною кількістю нулів
					counter.innerText = animObj.val.toFixed(decimals)
				}
			})
		})
	}
}